import { useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  MoreHorizontal,
  Calendar,
  User,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Task, TaskStatus, TaskPriority, TaskBoardData, TaskStats } from '@/types';
import { taskService } from '@/services';
import { useTaskBoardSync } from '@/hooks/useTaskSync';

// Board column status (excludes 'cancelled' which is not shown on board)
type BoardColumnStatus = Exclude<TaskStatus, 'cancelled'>;

interface TaskBoardProps {
  projectId: string;
  onTaskSelect?: (task: Task) => void;
}

const COLUMNS: { id: BoardColumnStatus; title: string; icon: React.ElementType }[] = [
  { id: 'todo', title: 'To Do', icon: Clock },
  { id: 'in_progress', title: 'In Progress', icon: AlertCircle },
  { id: 'in_review', title: 'In Review', icon: User },
  { id: 'done', title: 'Done', icon: CheckCircle2 },
];

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-800' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-800' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800' },
};

function TaskCard({
  task,
  onEdit,
  onDelete,
  onComplete,
  onReopen,
}: {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onComplete: (taskId: string) => void;
  onReopen: (taskId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityConfig = PRIORITY_CONFIG[task.priority];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'p-3 bg-background rounded-lg border shadow-sm cursor-grab active:cursor-grabbing',
        'hover:border-primary/50 transition-colors',
        task.isOverdue && 'border-red-500/50 bg-red-50/50'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium line-clamp-2">{task.title}</h4>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(task)}>
              Edit
            </DropdownMenuItem>
            {task.canComplete && (
              <DropdownMenuItem onClick={() => onComplete(task.id)}>
                Mark Complete
              </DropdownMenuItem>
            )}
            {task.canReopen && (
              <DropdownMenuItem onClick={() => onReopen(task.id)}>
                Reopen
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onDelete(task.id)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {task.description && (
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
          {task.description}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        <Badge className={cn('text-xs', priorityConfig.color)}>
          {priorityConfig.label}
        </Badge>
        {task.labels?.map((label) => (
          <Badge key={label} variant="outline" className="text-xs">
            {label}
          </Badge>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        {task.dueDate && (
          <div
            className={cn(
              'flex items-center gap-1',
              task.isOverdue && 'text-red-600 font-medium'
            )}
          >
            <Calendar className="h-3 w-3" />
            {new Date(task.dueDate).toLocaleDateString()}
          </div>
        )}
        {task.assigneeName && (
          <div className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {task.assigneeName}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskColumn({
  column,
  tasks,
  count,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onCompleteTask,
  onReopenTask,
}: {
  column: typeof COLUMNS[number];
  tasks: Task[];
  count: number;
  onAddTask: (status: TaskStatus) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onCompleteTask: (taskId: string) => void;
  onReopenTask: (taskId: string) => void;
}) {
  const Icon = column.icon;

  return (
    <Card className="flex flex-col h-full min-w-[280px] max-w-[320px]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Icon className="h-4 w-4" />
            {column.title}
            <Badge variant="secondary" className="ml-1">
              {count}
            </Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onAddTask(column.id)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-2">
        <ScrollArea className="h-full">
          <SortableContext
            items={tasks.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 p-1">
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={onEditTask}
                  onDelete={onDeleteTask}
                  onComplete={onCompleteTask}
                  onReopen={onReopenTask}
                />
              ))}
              {tasks.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8">
                  No tasks
                </div>
              )}
            </div>
          </SortableContext>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

interface TaskFormData {
  title: string;
  description: string;
  priority: TaskPriority;
  dueDate: string;
  status: TaskStatus;
}

export function TaskBoard({ projectId, onTaskSelect: _onTaskSelect }: TaskBoardProps) {
  // Note: _onTaskSelect reserved for future task detail view integration
  void _onTaskSelect;

  const [boardData, setBoardData] = useState<TaskBoardData>({
    todo: [],
    in_progress: [],
    in_review: [],
    done: [],
  });
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    status: 'todo',
  });

  // Tasks.md 동기화 훅
  const {
    hasTasksDocument,
    isSyncing,
    onBoardChange,
    ensureTasksDocument,
  } = useTaskBoardSync(projectId);

  const boardDataRef = useRef<TaskBoardData>(boardData);
  boardDataRef.current = boardData;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadBoard = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await taskService.getTaskBoard(projectId);
      // API 응답이 예상 구조인지 확인
      const newBoardData = response?.data ?? {
        todo: [],
        in_progress: [],
        in_review: [],
        done: [],
      };
      setBoardData(newBoardData);
      setStats(response?.meta?.stats ?? null);
      // Tasks.md 문서에 동기화
      onBoardChange(newBoardData);
    } catch (error) {
      console.error('Failed to load task board:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, onBoardChange]);

  useEffect(() => {
    loadBoard();
    // Tasks.md 문서가 없으면 생성
    ensureTasksDocument();
  }, [loadBoard, ensureTasksDocument]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = findTask(active.id as string);
    setActiveTask(task || null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeColumn = findColumn(activeId);
    const overColumn = findColumn(overId) || (COLUMNS.find((c) => c.id === overId)?.id as BoardColumnStatus | undefined);

    if (!activeColumn || !overColumn) return;

    if (activeColumn !== overColumn && COLUMNS.some((c) => c.id === overColumn)) {
      setBoardData((prev) => {
        const activeItems = [...(prev[activeColumn] || [])];
        const overItems = [...(prev[overColumn] || [])];
        const activeIndex = activeItems.findIndex((t: Task) => t.id === activeId);

        const [movedTask] = activeItems.splice(activeIndex, 1);
        movedTask.status = overColumn;
        overItems.push(movedTask);

        return {
          ...prev,
          [activeColumn]: activeItems,
          [overColumn]: overItems,
        };
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeColumn = findColumn(activeId);
    const overColumn = findColumn(overId) || (COLUMNS.find((c) => c.id === overId)?.id as BoardColumnStatus | undefined);

    if (!activeColumn || !overColumn) return;

    if (activeColumn === overColumn) {
      const items = boardData[activeColumn] || [];
      const oldIndex = items.findIndex((t: Task) => t.id === activeId);
      const newIndex = items.findIndex((t: Task) => t.id === overId);

      if (oldIndex !== newIndex) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        setBoardData((prev) => ({
          ...prev,
          [activeColumn]: newItems,
        }));

        // Update positions on server
        try {
          await taskService.reorderTasks(
            projectId,
            newItems.map((task: Task, i: number) => ({ id: task.id, position: i, status: activeColumn }))
          );
        } catch (error) {
          console.error('Failed to reorder tasks:', error);
          loadBoard();
        }
      }
    } else {
      // Task moved to different column
      try {
        await taskService.updateTaskStatus(projectId, activeId, overColumn);
      } catch (error) {
        console.error('Failed to update task status:', error);
        loadBoard();
      }
    }
  };

  const findTask = (id: string): Task | undefined => {
    for (const column of Object.values(boardData) as Task[][]) {
      const task = column.find((t: Task) => t.id === id);
      if (task) return task;
    }
    return undefined;
  };

  const findColumn = (taskId: string): BoardColumnStatus | undefined => {
    for (const col of COLUMNS) {
      const tasks = boardData[col.id];
      if (tasks && tasks.some((t: Task) => t.id === taskId)) return col.id;
    }
    return undefined;
  };

  const handleAddTask = (status: TaskStatus) => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: '',
      status,
    });
    setIsDialogOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      dueDate: task.dueDate || '',
      status: task.status,
    });
    setIsDialogOpen(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await taskService.deleteTask(projectId, taskId);
      loadBoard();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await taskService.completeTask(projectId, taskId);
      loadBoard();
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };

  const handleReopenTask = async (taskId: string) => {
    try {
      await taskService.reopenTask(projectId, taskId);
      loadBoard();
    } catch (error) {
      console.error('Failed to reopen task:', error);
    }
  };

  const handleSaveTask = async () => {
    try {
      if (editingTask) {
        await taskService.updateTask(projectId, editingTask.id, {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          dueDate: formData.dueDate || undefined,
        });
      } else {
        await taskService.createTask(projectId, {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          dueDate: formData.dueDate || undefined,
        });
      }
      setIsDialogOpen(false);
      loadBoard();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Stats Bar */}
      {stats && (
        <div className="flex items-center justify-between p-4 border-b bg-muted/30">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Total:</span>
              <Badge variant="secondary">{stats.total}</Badge>
            </div>
            {stats.overdue > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-red-600">{stats.overdue} overdue</span>
              </div>
            )}
          </div>

          {/* Tasks.md 동기화 상태 */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {isSyncing ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}
                  <span>
                    {hasTasksDocument
                      ? isSyncing
                        ? 'Tasks.md 동기화 중...'
                        : 'Tasks.md 연동됨'
                      : 'Tasks.md 미연결'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>칸반보드 변경사항이 Tasks.md 문서에 자동 동기화됩니다</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Board */}
      <div className="flex-1 overflow-x-auto p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 h-full">
            {COLUMNS.map((column) => (
              <TaskColumn
                key={column.id}
                column={column}
                tasks={boardData[column.id] || []}
                count={stats?.[column.id] ?? 0}
                onAddTask={handleAddTask}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onCompleteTask={handleCompleteTask}
                onReopenTask={handleReopenTask}
              />
            ))}
          </div>

          <DragOverlay>
            {activeTask && (
              <div className="p-3 bg-background rounded-lg border shadow-lg opacity-90">
                <h4 className="text-sm font-medium">{activeTask.title}</h4>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Task Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTask ? 'Edit Task' : 'Create Task'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Task title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Task description"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value: TaskPriority) =>
                    setFormData({ ...formData, priority: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTask} disabled={!formData.title.trim()}>
              {editingTask ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TaskBoard;
